import React from 'react';
import { Link } from 'react-router-dom';
import { FileSearch, ArrowRight, Clock, MessageSquareWarning, CheckCircle2 } from 'lucide-react';
import type { ExpertHomeData } from '../../../types/aai';
import { Card, MonoLabel, PageHeader } from './ui';

// The Expert's landing — review-queue counts + the freshest PRDs. The real
// workbench is /aai/prd-reviews.

const STAT_TONES = [
  { key: 'pending', label: 'Awaiting review', icon: Clock, cls: 'text-amber-600' },
  { key: 'changes_requested', label: 'Changes requested', icon: MessageSquareWarning, cls: 'text-red-500' },
  { key: 'approved', label: 'Approved', icon: CheckCircle2, cls: 'text-emerald-600' },
] as const;

const ExpertHome: React.FC<{ data: ExpertHomeData }> = ({ data }) => (
  <div className="max-w-3xl mx-auto">
    <PageHeader
      kicker="Engineering review"
      title="You're the gate between a spec and a build"
      subtitle="Nothing gets built without your sign-off."
    />

    <div className="grid grid-cols-3 gap-3 mb-4">
      {STAT_TONES.map(({ key, label, icon: Icon, cls }) => (
        <Card key={key} className="text-center">
          <Icon size={18} className={`mx-auto mb-1 ${cls}`} />
          <div className={`text-2xl font-semibold ${cls}`}>{data.counts[key]}</div>
          <div className="text-[11.5px] text-gray-500">{label}</div>
        </Card>
      ))}
    </div>

    <Card>
      <div className="flex items-center justify-between mb-3">
        <MonoLabel>Latest PRDs</MonoLabel>
        <Link to="/aai/prd-reviews" className="inline-flex items-center gap-1 text-[12px] font-semibold text-brand-600 hover:text-brand-700">
          Open the queue <ArrowRight size={13} />
        </Link>
      </div>
      {data.reviews.length === 0 ? (
        <p className="text-[13px] text-gray-400 text-center py-4">No PRDs drafted yet — you'll be notified the moment one needs review.</p>
      ) : (
        <div className="space-y-2">
          {data.reviews.map((r) => (
            <Link key={r.engagement_id} to="/aai/prd-reviews"
              className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 rounded-lg px-3.5 py-2.5">
              <FileSearch size={16} className="text-brand-500 shrink-0" />
              <span className="text-[13px] font-medium flex-1 min-w-0 truncate">{r.title}</span>
              <span className="text-[11px] font-semibold text-gray-400 capitalize shrink-0">{r.review.status.replace('_', ' ')}</span>
            </Link>
          ))}
        </div>
      )}
    </Card>
  </div>
);

export default ExpertHome;
